/* eslint-disable @typescript-eslint/naming-convention */
import { ILocation, IServices, isOpeningHours, LocationAddress } from '@pepperhq/types-sdk';
import { OrderScenario } from 'components/order/model/Order';
import {
    isArrayOf,
    isBoolean,
    isDefined,
    isNumber,
    isOptional,
    isOptionalArrayOf,
    isOptionalString,
    isString
} from 'lib/typeInference';

export enum DaysOfTheWeek {
    MONDAY = 'monday',
    TUESDAY = 'tuesday',
    WEDNESDAY = 'wednesday',
    THURSDAY = 'thursday',
    FRIDAY = 'friday',
    SATURDAY = 'saturday',
    SUNDAY = 'sunday'
}

export const DAYS_OF_THE_WEEK: DaysOfTheWeek[] = [
    DaysOfTheWeek.MONDAY,
    DaysOfTheWeek.TUESDAY,
    DaysOfTheWeek.WEDNESDAY,
    DaysOfTheWeek.THURSDAY,
    DaysOfTheWeek.FRIDAY,
    DaysOfTheWeek.SATURDAY,
    DaysOfTheWeek.SUNDAY
];

export enum LocationButtonActions {
    PREORDER = 'PREORDER',
    CHECKIN = 'CHECKIN',
    BROWSER = 'BROWSER',
    OPENING_TIMES = 'OPENING_TIMES',
    OPEN_TAB = 'OPEN_TAB',
    TABLE_ORDER = 'TABLE_ORDER',
    EVENTS = 'EVENTS'
}

export function isLocationButtonActions(action: any): action is LocationButtonActions {
    return isString(action) && action in LocationButtonActions;
}

export enum LocationButtonStyle {
    PRIMARY = 'PRIMARY',
    SECONDARY = 'SECONDARY'
}

export function isLocationButtonsStyle(style: any): style is LocationButtonStyle {
    return isString(style) && style in LocationButtonStyle;
}

export interface IButtonProperties {
    properties: {
        url: string;
    };
}

export function isButtonProperties(properties: any): properties is IButtonProperties {
    return isDefined(properties) && isString(properties.url);
}

export interface IButton {
    title: string;
    style: LocationButtonStyle;
    action: LocationButtonActions;
    properties?: IButtonProperties;
}

export function isButton(button: any): button is IButton {
    return (
        isDefined(button) &&
        isString(button.title) &&
        isLocationButtonsStyle(button.style) &&
        isLocationButtonActions(button.action) &&
        isOptional(isButtonProperties, button.properties)
    );
}

export enum LocationState {
    PENDING = 'PENDING',
    ACTIVE = 'ACTIVE',
    RETIRED = 'RETIRED'
}

export function isLocationState(state: any): state is string {
    return isString(state) && state in LocationState;
}

export type LocationService = keyof IServices;

export function isServices(services: any): services is IServices {
    return (
        isOptional(isBoolean, services.checkins) &&
        isOptional(isBoolean, services.offline_checkin) &&
        isBoolean(services.pre_order) &&
        isOptional(isBoolean, services.book_table) &&
        isOptional(isBoolean, services.book_room) &&
        isOptional(isBoolean, services.tab) &&
        isOptional(isBoolean, services.order_to_table) &&
        isOptional(isBoolean, services.order_to_table_nfc) &&
        isOptional(isBoolean, services.dine_in)
    );
}

export interface ILink {
    title?: string;
    parameters?: string;
    enabled?: boolean;
}

export interface ILinks {
    facebook?: ILink;
    twitter?: ILink;
    foursquare?: ILink;
    instagram?: ILink;
    uber?: ILink;
    hailo?: ILink;
    citymapper?: ILink;
}

export function isLink(link: any): link is ILink {
    return (
        isOptionalString(link.title) &&
        isOptionalString(link.parameters) &&
        isOptional(isBoolean, link.enabled)
    );
}

export function isLinks(links: any): links is ILinks {
    return (
        isDefined(links) &&
        isOptional(isLink, links.facebook) &&
        isOptional(isLink, links.twitter) &&
        isOptional(isLink, links.foursquare) &&
        isOptional(isLink, links.instagram) &&
        isOptional(isLink, links.uber) &&
        isOptional(isLink, links.hailo) &&
        isOptional(isLink, links.citymapper)
    );
}

export interface OrderNote {
    description?: string;
    title: string;
    channels?: {
        TERMINAL?: boolean;
        APP?: boolean;
        WEB?: boolean;
    };
}

export interface Location extends Omit<ILocation<string>, 'zones'> {
    address: LocationAddress;
    contacts?: LocationContacts;
    extended?: LocationExtended;
    state: LocationState;
    zones?: Record<string, number[][]>;
    distance?: number;
    durationInMins?: number;
    deliveryFee?: number;
}

export interface LocationContacts {
    email: string;
    phone: string;
}

export interface LocationButton {
    title: string;
    style: 'PRIMARY' | 'SECONDARY';
    action: LocationButtonActions;
    properties: {
        url: string;
    };
}

export interface LocationExtended {
    bookRoomId: string;
    bookTableId: string;
    secretDjId: string;
}

// v13 list responses use cursor-based pagination: `page.nextKey` is the forward cursor
// (absent/undefined => no more pages) and is passed back as `startKey` on the next request.
export interface LocationsPage {
    count: number;
    limit: number;
    startKey?: string;
    nextKey?: string;
}

export type LocationsSummary = LocationsPage | null;

function isLocationsPage(page: any): page is LocationsPage {
    return (
        !page ||
        (isNumber(page.count) &&
            isNumber(page.limit) &&
            isOptionalString(page.startKey) &&
            isOptionalString(page.nextKey))
    );
}

export function isLocation(location: any): location is Location {
    return (
        isDefined(location) &&
        isString(location.title) &&
        isOptional(isLocationState, location.state) &&
        isOptionalString(location.mainImageUrl) &&
        isOptionalString(location.listingImageUrl) &&
        isOptional(isOpeningHours, location.openingHours) &&
        isOptionalString(location.alternativeOpeningHoursText) &&
        isOptional(isServices, location.services) &&
        isOptional(isLinks, location.links) &&
        isOptional(isDefined, location.extended) &&
        isOptionalString(location.description) &&
        isOptional(isDefined, location.address) &&
        isOptional(isDefined, location.contacts) &&
        isOptional(isDefined, location.beacons) &&
        isOptionalString(location.VATnumber) &&
        isArrayOf(isNumber, location.geo) &&
        isOptionalArrayOf(isString, location.regions) &&
        isOptionalArrayOf(isString, location.tags)
    );
}

export function isLocations(locations: any[]): locations is Location[] {
    return !locations || !locations.length || isLocation(locations[0]);
}

export interface LocationsData {
    items: Location[];
    page: LocationsPage;
}

export function isLocationsData(data: any): data is LocationsData {
    return !data || (isLocations(data.items) && isLocationsPage(data.page));
}

export function getOrderNotes(location: Location | undefined, scenario: OrderScenario | undefined) {
    let _notes: OrderNote[] = [];
    if (!location || !scenario) {
        return _notes;
    }
    if (scenario === OrderScenario.PREORDER) {
        _notes = location.ordering?.extendedFields?.PREORDER?.note ?? [];
    }
    if (scenario === OrderScenario.DINE_IN) {
        _notes = location.ordering?.extendedFields?.DINE_IN?.note ?? [];
    }
    if (scenario === OrderScenario.DELIVERY) {
        _notes = location.ordering?.extendedFields?.DELIVERY?.note ?? [];
    }
    if (scenario === OrderScenario.ORDER_TO_TABLE || scenario === OrderScenario.TABLE) {
        _notes = location.ordering?.extendedFields?.ORDER_TO_TABLE?.note ?? [];
    }
    return _notes.filter(item => !isBoolean(item?.channels?.WEB) || item?.channels?.WEB);
}
